import React, { useEffect, useMemo, useState } from 'react';
import { getter } from './utils';
import { formatEntityDataField, getCaseInsensitiveValue } from 'shared/utils';
import moment from 'moment-timezone';
import { Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import SelectMUI from 'components/mui/select/select.component';
import Carousel from 'react-material-ui-carousel';
import { ArrowBackIcon, ArrowForwardIcon, CarrotDownIcon, CarrotUpIcon, CheckCircleIcon, CustomerInfoIcon, InformationIcon, ListAltBoundingSideIcon, VehicleFilledIcon, VehicleIcon, VehicleTagIcon } from 'components/mui';
import { Loader } from '@cinch/ui';

// Columns shown in source vehicle table header — excluded from expanded row fields
const sourceDefaultColumns = ['year', 'make', 'model', 'tag', 'tag_state', 'entity_source'];
function pivotVehicles(sourceVehicles) {
  const groups = {};
  sourceVehicles.forEach(sv => {
    const key = sv.matching_vehicle?.id || `unmatched_${sv.id}`;
    if (!groups[key]) {
      groups[key] = {
        matching_vehicle: sv.matching_vehicle || null,
        source_vehicles: []
      };
    }
    groups[key].source_vehicles.push(sv);
  });
  return Object.values(groups);
}
function getActiveSourceVehicle(group) {
  const svs = group.source_vehicles;
  const activeId = group.matching_vehicle?.active_vehicle;
  const byId = svs.find(sv => sv.id === activeId);
  if (byId) return byId;

  // Fallback: last updated source vehicle
  return svs.reduce((latest, sv) => {
    if (!latest) return sv;
    return sv.updated_when > latest.updated_when ? sv : latest;
  }, null);
}
function renderNullable(value) {
  return value !== null && value !== undefined && value !== '' ? value : <Typography variant="nullData" color="#BEBEBE">
      <span>--</span>
    </Typography>;
}
function renderFieldValue(field, value) {
  if (value === null || value === undefined || value === '') return null;
  if (field.type === 'datetime') return moment(value).local().format('MMM D, YYYY h:mm A');
  if (field.type === 'date') return moment(value).format('MMM D, YYYY');
  return value;
}
function SectionHeader({
  icon,
  title,
  expanded,
  onToggle
}) {
  return <Box onClick={onToggle} sx={{
    'display': 'flex',
    'alignItems': 'center',
    'justifyContent': 'space-between',
    'px': '16px',
    'py': '10px',
    'cursor': 'pointer',
    'borderTop': '1px solid #EDEFF1',
    'backgroundColor': expanded ? '#F3F6FC' : '#FAFAFA',
    ':hover': {
      backgroundColor: '#F3F6FC'
    }
  }}>
      <Stack direction="row" spacing="10px" sx={{
      alignItems: 'center'
    }}>
        {icon}
        <Typography variant="tableHeader">{title}</Typography>
      </Stack>
      {expanded ? <CarrotUpIcon /> : <CarrotDownIcon />}
    </Box>;
}
export function VehiclesComponent(props) {
  const {
    vehicles,
    availableModelsFields,
    availableModels,
    _detailsTabs,
    handleDetailsTab,
    selectedDetailsTab,
    isLoading,
    selectedVehicle
  } = props;
  const [selectedVehicleIndex, setSelectedVehicleIndex] = useState(0);
  const [section2Expanded, setSection2Expanded] = useState(true);
  const [section3Expanded, setSection3Expanded] = useState(false);
  const [section4Expanded, setSection4Expanded] = useState(false);
  const [expandedSourceVehicle, setExpandedSourceVehicle] = useState(null);
  const [onSourceHover, setOnSourceHover] = useState(null);
  const vehicleGroups = useMemo(() => pivotVehicles(vehicles || []), [vehicles]);
  useEffect(() => {
    if (!selectedVehicle || !vehicleGroups.length) return;
    const index = vehicleGroups.findIndex(g => (g.matching_vehicle?.id || g.source_vehicles[0]?.id) === selectedVehicle);
    if (index > -1) setSelectedVehicleIndex(index);
  }, [selectedVehicle, vehicleGroups]);
  const allVehicleFields = availableModelsFields?.find(model => model.id === 'vehicle')?.fields || [];

  // Section 2: source vehicle fields (active vehicle) — excludes matching_vehicle__ and nhtsa_spec fields
  const activeVehicleFields = allVehicleFields.filter(f => !f.id.startsWith('matching_vehicle__'));

  // Section 3: NHTSA fields
  const nhtsaFields = allVehicleFields.filter(f => f.id.startsWith('matching_vehicle__nhtsa_spec__'));

  // Section 4: source vehicle expanded row fields — same as activeVehicleFields minus table defaults
  const sourceExpandedFields = activeVehicleFields.filter(f => !sourceDefaultColumns.includes(f.id));
  const handleCarouselChange = now => {
    setSelectedVehicleIndex(now);
    setExpandedSourceVehicle(null);
  };
  return <>
      <Stack sx={{
      'height': '100%',
      'background': '#E4E9F1',
      'borderBottomLeftRadius': '28px',
      'borderBottomRightRadius': '28px',
      'overflowY': 'scroll',
      'scrollbarWidth': 'thin',
      '&::-webkit-scrollbar': {
        width: '0.4em'
      },
      '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
        marginTop: '25px',
        marginBottom: '25px'
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#888'
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: '#555'
      }
    }} className="d-flex h-100" spacing="14px">
        <Box className="d-flex w-100 justify-content-between" sx={{
        position: 'relative',
        pt: '14px',
        pb: '38px',
        display: 'flex'
      }}>
          <span />
          <Box sx={{
          position: 'absolute',
          left: '37.5%'
        }}>
            <SelectMUI height="36px" width="180px" top="6px" zIndex={9001} options={_detailsTabs} value={selectedDetailsTab || ''} onChange={opt => handleDetailsTab(opt.key)} />
          </Box>
          {vehicleGroups?.length > 0 && <Box sx={{
          position: 'absolute',
          left: '77%',
          top: '24px'
        }}>
              {'' + (selectedVehicleIndex + 1) + ' of ' + vehicleGroups.length + ' Items'}
            </Box>}
        </Box>

        <Stack>
          {isLoading ? <Loader sx={{
          pt: '25px'
        }} /> : <>
              {vehicleGroups?.length > 0 ? <Carousel index={selectedVehicleIndex} navButtonsAlwaysVisible cycleNavigation={false} autoPlay={false} animation="slide" indicators={false} PrevIcon={<ArrowBackIcon fill="#fff" />} NextIcon={<ArrowForwardIcon fill="#fff" />} onChange={handleCarouselChange}>
                  {vehicleGroups.map((group, i) => {
              const mv = group.matching_vehicle;
              const sv0 = group.source_vehicles[0];
              const activeSV = getActiveSourceVehicle(group);
              const isActiveFallback = mv?.active_vehicle && !group.source_vehicles.find(sv => sv.id === mv.active_vehicle);

              // Section 1 defaults from matching_vehicle with fallback to first source vehicle
              const year = mv?.year ?? sv0?.year;
              const make = mv?.make ?? sv0?.make;
              const model = mv?.model ?? sv0?.model;
              const vin = mv?.vin ?? sv0?.vin;
              const tag = mv?.tag ?? sv0?.tag;
              const tagState = mv?.tag_state ?? sv0?.tag_state;
              const tagDisplay = tagState && tag ? `${tagState} - ${tag}` : tag || tagState || null;
              const hasNhtsa = nhtsaFields.length > 0 && mv?.nhtsa_spec != null;
              return <Box key={i} sx={{
                px: '50px'
              }}>
                        <Paper sx={{
                  'borderRadius': '10px',
                  'overflowY': 'scroll',
                  'scrollbarWidth': 'thin',
                  '&::-webkit-scrollbar': {
                    width: '0.4em'
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    marginTop: '10px',
                    marginBottom: '10px'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#888'
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#555'
                  },
                  'height': '68vh'
                }}>
                          {/* ── Section 1: Header (Company Vehicle Data) ── */}
                          <Table>
                            <TableBody>
                              <TableRow>
                                <TableCell>
                                  <Stack spacing="10px" direction="row" sx={{
                            alignItems: 'center'
                          }}>
                                    <VehicleIcon />
                                    <Typography variant="tableHeader">Vehicle</Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  {year || make || model ? <Typography>{[year, make, model].filter(Boolean).join(' ')}</Typography> : renderNullable(null)}
                                </TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell>
                                  <Stack spacing="10px" direction="row" sx={{
                            alignItems: 'center'
                          }}>
                                    <VehicleTagIcon />
                                    <Typography variant="tableHeader">Tags</Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell>{renderNullable(tagDisplay)}</TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell>
                                  <Stack spacing="10px" direction="row" sx={{
                            alignItems: 'center'
                          }}>
                                    <CustomerInfoIcon />
                                    <Typography variant="tableHeader">VIN</Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell>{renderNullable(vin)}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>

                          {/* ── Section 2: Active Vehicle ── */}
                          {availableModels.indexOf('vehicle') > -1 && activeSV && <>
                              <SectionHeader icon={<InformationIcon />} title={isActiveFallback ? 'Additional Details (last updated)' : 'Additional Details'} expanded={section2Expanded} onToggle={() => setSection2Expanded(!section2Expanded)} />
                              {section2Expanded && <Table>
                                  <TableBody>
                                    {activeVehicleFields.map((field, index) => {
                          const fieldValue = activeSV[field.id];
                          const rendered = renderFieldValue(field, fieldValue);
                          if (rendered !== null && rendered !== undefined) {
                            return <TableRow key={field.id + index}>
                                            <TableCell>
                                              <Typography variant="tableHeader">{field.name}</Typography>
                                            </TableCell>
                                            <TableCell>{rendered}</TableCell>
                                          </TableRow>;
                          }
                          if (field.id.startsWith('entity_data__')) {
                            return <TableRow key={field.id + index}>
                                            <TableCell>
                                              <Typography variant="tableHeader">{field.name}</Typography>
                                            </TableCell>
                                            <TableCell>
                                              {(() => {
                                  const keys = field.id.split('__');
                                  const rawValue = keys.length === 2 ? getCaseInsensitiveValue(activeSV.entity_data, keys[1]) : getter(activeSV, keys);
                                  const formatted = formatEntityDataField(field, rawValue);
                                  return formatted !== null && formatted !== undefined ? formatted : <Typography sx={{
                                    color: '#BEBEBE'
                                  }}>--</Typography>;
                                })()}
                                            </TableCell>
                                          </TableRow>;
                          }
                          return <React.Fragment key={field.id} />;
                        })}
                                  </TableBody>
                                </Table>}
                            </>}

                          {/* ── Section 3: NHTSA Details ── */}
                          {hasNhtsa && <>
                              <SectionHeader icon={<CheckCircleIcon />} title="NHTSA Details" expanded={section3Expanded} onToggle={() => setSection3Expanded(!section3Expanded)} />
                              {section3Expanded && <Table>
                                  <TableBody>
                                    {nhtsaFields.map((field, index) => {
                          const keys = field.id.split('__');
                          const rawValue = getter(group, keys);
                          const rendered = renderFieldValue(field, rawValue);
                          return rendered !== null && rendered !== undefined ? <TableRow key={field.id + index}>
                                          <TableCell>
                                            <Typography variant="tableHeader">{field.name}</Typography>
                                          </TableCell>
                                          <TableCell>{rendered}</TableCell>
                                        </TableRow> : <React.Fragment key={field.id} />;
                        })}
                                  </TableBody>
                                </Table>}
                            </>}

                          {/* ── Section 4: Source Vehicles ── */}
                          {group.source_vehicles?.length > 0 && <>
                              <SectionHeader icon={<ListAltBoundingSideIcon />} title={`Source Vehicles (${group.source_vehicles.length})`} expanded={section4Expanded} onToggle={() => {
                      setSection4Expanded(!section4Expanded);
                      setExpandedSourceVehicle(null);
                    }} />
                              {section4Expanded && <Table>
                                  <colgroup>
                                    <col style={{
                          width: '28%'
                        }} />
                                    <col style={{
                          width: '22%'
                        }} />
                                    <col style={{
                          width: '28%'
                        }} />
                                    <col style={{
                          width: '14%'
                        }} />
                                    <col style={{
                          width: '8%'
                        }} />
                                  </colgroup>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>
                                        <Typography variant="tableHeader">Vehicle</Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="tableHeader">Tag</Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="tableHeader">Source</Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="tableHeader">Status</Typography>
                                      </TableCell>
                                      <TableCell />
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {group.source_vehicles.map(sv => {
                          const isActive = mv?.active_vehicle === sv.id;
                          const isExpanded = expandedSourceVehicle === sv.id;
                          const svYMM = [sv.year, sv.make, sv.model].filter(Boolean).join(' ') || '--';
                          const svTag = sv.tag_state && sv.tag ? `${sv.tag_state} - ${sv.tag}` : sv.tag || sv.tag_state || '--';
                          return <React.Fragment key={sv.id}>
                                          <TableRow sx={{
                              'cursor': 'pointer',
                              ':hover': {
                                backgroundColor: '#FAFAFA'
                              }
                            }} onClick={() => setExpandedSourceVehicle(isExpanded ? null : sv.id)} onMouseEnter={() => setOnSourceHover(sv.id)} onMouseLeave={() => setOnSourceHover(null)}>
                                            <TableCell>
                                              <Tooltip title={sv.vin || 'No VIN'} placement="top">
                                                <Typography sx={{
                                    cursor: 'default'
                                  }}>{svYMM}</Typography>
                                              </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                              <Typography>{svTag}</Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography>{sv.entity_source || '--'}</Typography>
                                            </TableCell>
                                            <TableCell>
                                              {isActive && <Chip label="ACTIVE" size="small" sx={{
                                  backgroundColor: '#3898D9',
                                  color: '#fff',
                                  fontWeight: 600,
                                  fontSize: '10px'
                                }} />}
                                            </TableCell>
                                            <TableCell sx={{
                                py: '0px'
                              }}>
                                              {onSourceHover === sv.id && (isExpanded ? <CarrotUpIcon /> : <CarrotDownIcon />)}
                                            </TableCell>
                                          </TableRow>

                                          {isExpanded && sourceExpandedFields.map((field, fIdx) => {
                              const fieldValue = sv[field.id];
                              const rendered = renderFieldValue(field, fieldValue);
                              if (rendered !== null && rendered !== undefined) {
                                return <TableRow key={field.id + fIdx} sx={{
                                  'backgroundColor': '#FAFAFA',
                                  'cursor': 'pointer'
                                }} onClick={() => setExpandedSourceVehicle(null)}>
                                                    <TableCell>
                                                      <Typography variant="tableHeader">{field.name}</Typography>
                                                    </TableCell>
                                                    <TableCell colSpan={4}>{rendered}</TableCell>
                                                  </TableRow>;
                              }
                              if (field.id.startsWith('entity_data__')) {
                                return <TableRow key={field.id + fIdx} sx={{
                                  'backgroundColor': '#FAFAFA',
                                  'cursor': 'pointer'
                                }} onClick={() => setExpandedSourceVehicle(null)}>
                                                    <TableCell>
                                                      <Typography variant="tableHeader">{field.name}</Typography>
                                                    </TableCell>
                                                    <TableCell colSpan={4}>
                                                      {(() => {
                                      const keys = field.id.split('__');
                                      const rawValue = keys.length === 2 ? getCaseInsensitiveValue(sv.entity_data, keys[1]) : getter(sv, keys);
                                      const formatted = formatEntityDataField(field, rawValue);
                                      return formatted !== null && formatted !== undefined ? formatted : <Typography sx={{
                                        color: '#BEBEBE'
                                      }}>--</Typography>;
                                    })()}
                                                    </TableCell>
                                                  </TableRow>;
                              }
                              return <React.Fragment key={field.id} />;
                            })}
                                        </React.Fragment>;
                        })}
                                  </TableBody>
                                </Table>}
                            </>}
                        </Paper>
                      </Box>;
            })}
                </Carousel> : <Stack>
                  <Box sx={{
              display: 'flex',
              alignItems: 'center',
              alignContent: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%'
            }}>
                    <Typography>Customer has no vehicles</Typography>
                  </Box>
                </Stack>}
            </>}
        </Stack>
      </Stack>
    </>;
}